<template>
    <div>
        <h1 class="display-heading">Upload Doctor's Prescription</h1>

        <p>You may upload your prescription list via photo or scanned copy. And then you can purchase the drugs through our system.</p>
        <p>
            Please make sure that your prescription list clearly shown the information below:
            <ol>
                <li>Doctor's Name</li>
                <li>Clinic / Hospital Name</li>
                <li>Clinic / Hospital Address</li>
                <li>Doctor's Contact Number</li>
                <li>Doctor's Signature</li>
                <li>Medicine Names and Dosages</li>
            </ol>
        </p>
        <p>
            <b>Note</b>: We reserve our rights to decline this purchase on both regulatory and business grounds.
        </p>

        <div class="mb-5">
            <div class="card">
                <div class="card-header">Upload Doctor's Prescription</div>
                <div class="card-body">
                    <form data-vv-scope="prescription-upload" @submit.prevent="validatePrescriptionForm">
                        <div>
                            <div v-if="isFamily" class="form-group row">
                                <label class="col-sm-3 col-xl-2 col-form-label">Patient Name</label>
                                <div class="col-sm-9 col-xl-10">
                                <select placeholder="Patient" name="patient" v-model="uploadForm.dependentId"
                                    data-vv-as="patient" data-vv-validate-on="blur"
                                    v-validate="'required'" :class="{
                                    'form-control': true,
                                    'is-invalid':  validationErrors.has('prescription-upload.patient')
                                }">
                                    <option :value="null" disabled hidden>Please Select...</option>
                                    <option v-for="(dependent, index) in dependentList" :key="index"
                                        :value="dependent.userId">
                                        {{dependent.fullName}}
                                    </option>
                                </select>
                                    <div v-show="validationErrors.has('prescription-upload.patient')" class="invalid-feedback">
                                        {{ validationErrors.first('prescription-upload.patient') }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-sm-3 col-xl-2 col-form-label">Prescription File</label>
                                <div class="col-sm-9 col-xl-10">
                                    <input id="uploadFileInput" type="file" v-validate="'required|image'" data-vv-as="prescription file"
                                        name="prescriptionFile" :class="{'is-invalid':  validationErrors.has('prescription-upload.prescriptionFile')}">
                                    <div v-show="validationErrors.has('prescription-upload.prescriptionFile')" class="invalid-feedback">
                                        {{ validationErrors.first('prescription-upload.prescriptionFile') }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-sm-3 col-xl-2 col-form-label">Remarks</label>
                                <div class="col-sm-9 col-xl-10">
                                    <textarea v-model="uploadForm.remarks" class="form-control" rows="3">
                                    </textarea>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                                    <p v-show="isSubmitMessageShown" :class="{
                                        'text-danger': isSubmitMessageError
                                    }">
                                        {{submitMessage}}
                                    </p>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                                    <button type="button" class="btn btn-primary btn-primary-insv" @click="shouldShowLowCreditMessage">Confirm
                                        and Upload Prescription</button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <popup-modal ref="popupModalConfirmMailingAddress" :hasCloseButton="false" title="Confirm Mailing Address">
            <template slot="content">
               <p>Please confirm your mailing address. The medicine package will be sent to the following address after the payment is made.</p>

               <form data-vv-scope="confirm-address" @submit.prevent="validateForm">
                   <div id="confirmAddressForm" :class="{
                       'is-editing': mailingAddress.isEditing
                   }">
                       <div class="form-group">
                            <label>Address Line 1</label>
                            <input type="text" name="addressLine1" data-vv-as="address line 1" data-vv-validate-on="blur"
                                placeholder="Address Line 1" v-model="mailingAddress.addressLine1" v-validate="'required'" :class="{
                                'form-control': mailingAddress.isEditing,
                                'form-control-plaintext': !mailingAddress.isEditing,
                                'is-invalid':  validationErrors.has('confirm-address.addressLine1')
                            }" :readonly="!mailingAddress.isEditing">

                            <div v-show="validationErrors.has('confirm-address.addressLine1')" class="invalid-feedback">
                                {{ validationErrors.first('confirm-address.addressLine1') }}
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Address Line 2 (optional)</label>
                            <input type="text" placeholder="Address Line 2" v-model="mailingAddress.addressLine2" :class="{
                                'form-control': mailingAddress.isEditing,
                                'form-control-plaintext': !mailingAddress.isEditing
                            }" :readonly="!mailingAddress.isEditing">
                        </div>

                        <div class="form-group">
                            <label>City</label>
                            <input type="text" name="city" data-vv-validate-on="blur" placeholder="City" v-model="mailingAddress.addressCity"
                                v-validate="'required'" :class="{
                                'form-control': mailingAddress.isEditing,
                                'form-control-plaintext': !mailingAddress.isEditing,
                                'is-invalid':  validationErrors.has('confirm-address.city')
                            }" :readonly="!mailingAddress.isEditing">
                            <div v-show="validationErrors.has('confirm-address.city')" class="invalid-feedback">
                                {{ validationErrors.first('confirm-address.city') }}
                            </div>
                        </div>

                        <div class="form-group">
                            <label>State</label>
                            <select name="state" data-vv-validate-on="blur" v-model="mailingAddress.addressState" v-validate="'required'"
                                :class="{
                                'form-control': mailingAddress.isEditing,
                                'form-control-plaintext': !mailingAddress.isEditing,
                                'is-invalid':  validationErrors.has('confirm-address.state')
                            }" :disabled="!mailingAddress.isEditing">
                                <option :value="null">Choose...</option>
                                <option v-for="(state, index) in malaysiaStates" :key="index" :value="state.value">
                                    {{state.text}}
                                </option>
                            </select>
                            <div v-show="validationErrors.has('confirm-address.state')" class="invalid-feedback">
                                {{ validationErrors.first('confirm-address.state') }}
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Postal Code</label>
                            <input type="text" name="postcode" placeholder="Postal Code" data-vv-validate-on="blur" v-model="mailingAddress.addressPostcode"
                                v-validate="'required'" :class="{
                                'form-control': mailingAddress.isEditing,
                                'form-control-plaintext': !mailingAddress.isEditing,
                                'is-invalid':  validationErrors.has('confirm-address.postcode')
                            }" :readonly="!mailingAddress.isEditing">
                            <div v-show="validationErrors.has('confirm-address.postcode')" class="invalid-feedback">
                                {{ validationErrors.first('confirm-address.postcode') }}
                            </div>
                        </div>
                   </div>
               </form>
               <p v-if="mailingAddress.isSaving">
                   {{mailingAddress.savingText}}
               </p>
           </template>
           <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="proceedConfirmMailingAddress">
                    <template v-if="!mailingAddress.isEditing">
                        Confirm Address
                    </template>
                    <template v-else>
                        Update and Proceed
                    </template>
                </button>
                <button v-if="!mailingAddress.isEditing" type="button" class="btn btn-primary" @click="updateMailingAddress">Update Address</button>
           </template>
        </popup-modal>

        <popup-modal ref="popupModalLowCreditMessage" :hasCloseButton="false" title="Low Company Credit Balance">
            <template slot="content">
                <p>
                    Your company credit balance is currently low. You may be required to make payment either via FPX or credit card after this prescription has been reviewed.
                </p>
                <p>
                    Do you still want to proceed?
                </p>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="proceedLowCredit">Proceed Uploading Prescription</button>
                <button type="button" class="btn btn-secondary" @click="cancelLowCredit">Cancel</button>
            </template>
        </popup-modal>

        <popup-modal ref="popupModalConfirmation" :hasCloseButton="false" title="Disclaimer">
           <template slot="content">
               <p>Please understand and agree to all of our <a :href="termsAndConditionPatientsUrl" target="_blank">Terms & Conditions</a> before proceeding.</p>
           </template>
           <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="disclaimerProceed">Agreed & Proceed</button>
                <button type="button" class="btn btn-secondary" @click="disclaimerCancel">Cancel</button>
           </template>
        </popup-modal>
    </div>
</template>

<script>
import malaysiaStates from '@/config/malaysiaStates';
import popupModal from '@/components/popup-modal';
export default {
    'name': 'UserUploadPrescription',
    'components': {
        popupModal
    },
    'computed': {
        termsAndConditionPatientsUrl () {
            return this.$store.state.operator.termsAndConditionPatientsUrl;
        },
        isFamily () {
            return this.$route.name === 'family-upload-prescription';
        },
        uploadApi () {
            if (this.isFamily) {
                return 'Prescription/UploadFamilyPrescription';
            }
            return 'Prescription/UploadPrescription';
        },
        listPath () {
            if (this.isFamily) {
                return 'family-prescription-list';
            }
            return 'user-prescription-list';
        }
    },
    data () {
        return {
            malaysiaStates,
            'uploadForm': {
                'remarks': null,
                'dependentId': null
            },
            'isSubmitMessageShown': false,
            'isSubmitMessageError': false,
            'submitMessage': '',
            'mailingAddress': {
                'savingText': '',
                'isSaving': false,
                'isEditing': false,
                'addressLine1': '',
                'addressLine2': '',
                'addressCity': '',
                'addressState': '',
                'addressPostcode': ''
            },
            'isPrescriptionLowCredit': false,
            'dependentList': []
        };
    },
    mounted () {
        this.axios.get('Profile/GetMyProfile').then(({ data }) => {
            if (data.data) {
                this.mailingAddress.addressLine1 = data.data.mal1;
                this.mailingAddress.addressLine2 = data.data.mal2;
                this.mailingAddress.addressCity = data.data.mac;
                this.mailingAddress.addressState = data.data.mas;
                this.mailingAddress.addressPostcode = data.data.map;
            }
        });

        this.axios.get('CompanyCredit/CheckLowCredit').then(({
            data
        }) => {
            if (data.data) {
                this.isPrescriptionLowCredit = data.data.lcp;
            }
        });

        if (this.isFamily) {
            this.axios.get('UserDependent/GetDependentList').then(({ data }) => {
                if (data.data) {
                    this.dependentList = data.data.map((dependent) => ({
                        'userId': dependent.ui,
                        'fullName': dependent.fn
                    }));
                }
            });
        }
    },
    'methods': {
        validatePrescriptionForm () {
            return this.$validator.validateAll('prescription-upload');
        },
        shouldShowLowCreditMessage () {
            if (this.isPrescriptionLowCredit) {
                this.$refs.popupModalLowCreditMessage.show();
            } else {
                this.showConfirmMailingAddress();
            }
        },
        proceedLowCredit () {
            this.$refs.popupModalLowCreditMessage.hide().then(() => {
                this.showConfirmMailingAddress();
            });
        },
        cancelLowCredit () {
            this.$refs.popupModalLowCreditMessage.hide();
        },
        showDisclaimer () {
            this.$refs.popupModalConfirmation.show();
        },
        disclaimerProceed () {
            this.$refs.popupModalConfirmation.hide().then(() => {
                this.uploadPrescription();
            });
        },
        disclaimerCancel () {
            this.$refs.popupModalConfirmation.hide();
        },
        uploadPrescription () {
            this.isSubmitMessageShown = true;
            this.isSubmitMessageError = false;
            this.validatePrescriptionForm().then((isValidate) => {
                if (isValidate) {
                    let reportFile = document.querySelector('#uploadFileInput');
                    let formData = new FormData();
                    if (this.isFamily) {
                        formData.append('DependentUserId', this.uploadForm.dependentId);
                    }
                    formData.append('PrescriptionFile', reportFile.files[0]);
                    if (this.uploadForm.remarks) {
                        formData.append('Remarks', this.uploadForm.remarks);
                    }
                    this.submitMessage = 'Uploading and saving prescription list...';
                    this.axios.post(this.uploadApi, formData, {
                        'headers': {
                            'Content-Type': 'multipart/form-data'
                        } }).then(() => {
                        this.submitMessage = 'Successfully uploaded prescription list!';

                        this.$router.push({
                            'name': this.listPath
                        });
                    }).catch((error) => {
                        this.isSubmitMessageError = true;
                        if (error.response.data) {
                            this.submitMessage = error.response.data.message;
                        } else {
                            this.submitMessage = 'Error saving prescription list. Please try again.';
                        }
                    });
                } else {
                    this.isSubmitMessageError = true;
                    this.submitMessage = 'You have error in the form. Please check your input(s) before submitting the form.';
                }
            });
        },
        showConfirmMailingAddress () {
            this.validatePrescriptionForm().then((isValidate) => {
                if (isValidate) {
                    this.mailingAddress.isSaving = false;
                    this.mailingAddress.savingText = '';
                    this.mailingAddress.isEditing = false;
                    this.$refs.popupModalConfirmMailingAddress.show();
                }
            });
        },
        proceedConfirmMailingAddress () {
            if (this.mailingAddress.isEditing) {
                this.validateForm().then((isValid) => {
                    if (isValid) {
                        this.mailingAddress.isSaving = true;
                        this.mailingAddress.savingText = 'Updating your mailing address...';

                        this.axios.post('Profile/UpdateMailingAddress', {
                            'mal1': this.mailingAddress.addressLine1,
                            'mal2': this.mailingAddress.addressLine2,
                            'mac': this.mailingAddress.addressCity,
                            'mas': this.mailingAddress.addressState,
                            'map': this.mailingAddress.addressPostcode
                        }).then(() => {
                            this.$refs.popupModalConfirmMailingAddress.hide().then(() => {
                                this.showDisclaimer();
                            });
                        }).catch(() => {
                            this.mailingAddress.savingText = 'Error updating your mailing address. Please try again.';
                        });
                    }
                });
            } else {
                this.$refs.popupModalConfirmMailingAddress.hide().then(() => {
                    this.showDisclaimer();
                });
            }
        },
        updateMailingAddress () {
            this.mailingAddress.isEditing = true;

            this.$nextTick(() => {
                if (document.querySelectorAll('#confirmAddressForm input').length > 0) {
                    document.querySelectorAll('#confirmAddressForm input')[0].focus();
                }
            });
        },
        validateForm () {
            return this.$validator.validateAll('confirm-address');
        }
    }
};
</script>
